import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { PortalDetailShell } from "../components/PortalDetailShell";
import { StatusPill } from "../components/StatusPill";
import { usePortalAuth } from "../PortalAuthContext";
import { portalTabPathFor } from "./RecipientDashboardPage";
import { portalPackagesApi } from "../api/packages";
import { getItemStatusDisplay, isCompletedStatus } from "../lib/statusMap";
import { staffNoteFor } from "../lib/itemStatus";
import { portalLoginPathFor } from "../lib/returnTo";
import { PageHeader, ListCard, EmptyState } from "@/design-system/components/ui";
import type { PortalItemDto, PortalPackageDto } from "../api/types";

// External Intake & Collection v1.0 PR 4 §2.2 — "My documents" view.
// Renders the session's items only (server enforces the package scope on
// the GET). Each row shows the plain-language status, the reviewer note
// while a response is owed, and links into the appropriate fulfilment
// surface based on Kind. A visitor with no portal session is sent to
// sign-in with a returnTo back here (A17).

export function PackageView() {
  const { packageId } = useParams<{ packageId: string }>();
  const auth = usePortalAuth();
  const location = useLocation();
  const [pkg, setPkg] = useState<PortalPackageDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!packageId) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await portalPackagesApi.get(packageId);
        if (!cancelled) setPkg(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load this request.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [packageId]);

  const progress = useMemo(() => {
    if (!pkg) return { total: 0, done: 0 };
    const total = pkg.items.length;
    // Count SubmittedForReview alongside isCompletedStatus so a fully-submitted
    // package isn't shown as "0 of N" while staff are still reviewing.
    const done = pkg.items.filter(
      (i) => isCompletedStatus(i.status) || i.status === "SubmittedForReview",
    ).length;
    return { total, done };
  }, [pkg]);

  const allDone = pkg && progress.total > 0 && progress.done === progress.total;

  if (!auth.loading && !auth.me) {
    return <Navigate to={portalLoginPathFor(location.pathname + location.search)} replace />;
  }

  const content = (
    <>
      {/* Post-Rollout PR 4 (PROB-28b / Commit G) — this per-package view
          stays mounted for zero-history arrivals (a bookmark, or the e-sign
          break-out page's top.location.replace), but the Requests tab is the
          canonical home. Point landers back to it, matching the per-item
          "Back to checklist" affordance.

          The destination MUST carry `?tab=requests`: a bare dashboard path
          lands on tabs[0] (Home) and silently reproduces the bug this link
          was written to avoid. portalTabPathFor owns that composition — and
          is null-safe because this content is built before PortalChrome's
          auth.me guard runs. */}
      <Link
        to={portalTabPathFor(auth.me, "requests")}
        className="mb-space-4 inline-block text-small underline text-onyx"
        data-testid="package-back-to-requests"
      >
        ← Back to requests
      </Link>

      <PageHeader title={pkg ? `Checklist for ${pkg.label}` : "Checklist"} />
      {pkg && (
        <p className="mt-space-2 text-small text-onyx m-0" data-testid="progress-summary">
          <strong>{progress.done}</strong> of <strong>{progress.total}</strong> complete
        </p>
      )}

      {error && <p className="text-small text-status-error mt-space-3" role="alert">{error}</p>}

      {allDone && (
        <div className="mt-space-4 p-space-4 border border-gold bg-gold/10 rounded-md" role="status">
          <h2 className="text-h4 font-display text-onyx m-0">You're all done — thank you!</h2>
          <p className="text-small text-onyx m-0 mt-space-2">
            We have everything we need. We'll be in touch if anything else comes up.
          </p>
        </div>
      )}

      {pkg && pkg.items.length === 0 && (
        <div className="mt-space-4">
          <EmptyState
            icon={CheckCircle2}
            title="Nothing to do here"
            description="There are no items in this request right now."
          />
        </div>
      )}

      {pkg && pkg.items.length > 0 && (
        <ul className="mt-space-4 flex flex-col gap-space-3 list-none p-0">
          {pkg.items.map((item) => (
            <ItemRow key={item.id} packageId={pkg.id} item={item} />
          ))}
        </ul>
      )}
    </>
  );

  return <PortalDetailShell pageTitle="My documents">{content}</PortalDetailShell>;
}

interface ItemRowProps {
  packageId: string;
  item: PortalItemDto;
}

function ItemRow({ packageId, item }: ItemRowProps) {
  const display = getItemStatusDisplay(item.status);
  const completed = isCompletedStatus(item.status);
  const staffNote = staffNoteFor(item);
  return (
    <li>
      <ListCard
        to={`/portal/packages/${packageId}/items/${item.id}`}
        title={item.templateName}
        linkLabel={item.templateName}
        meta={item.templateDescription || undefined}
        status={<StatusPill status={item.status} />}
      >
        <p className="m-0 text-caption text-gray-mid">{display.description}</p>
        {staffNote && (
          <p
            className="m-0 mt-space-1 text-caption italic text-status-warning"
            data-testid="reviewer-note"
          >
            Reviewer note: {staffNote}
          </p>
        )}
        {!completed && item.status !== "Waived" && (
          <span className="mt-space-2 inline-block self-start text-caption text-gold-shadow underline">
            {item.status === "SubmittedForReview" ? "View status" : "Open"}
          </span>
        )}
      </ListCard>
    </li>
  );
}
